import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MarketplaceTokenRequest } from 'src/app/models/marketplace-token-request.model';
import { ActivateSubscriptionRequest, CreateSubscription, SubscriptionPlansRequest } from 'src/app/interfaces/services/subscription.interface';
import { CloudPlan } from 'src/app/models/cloud-plan.model';
import { Observable, of, throwError, Timestamp } from 'rxjs';
import { catchError, delay, map, tap } from 'rxjs/operators';
import { GetSigninUrl } from 'src/app/interfaces/services/user.interface';
import { CloudAuthenticationService } from 'src/app/services/cloud-authentication.service';
import { environment } from 'src/environments/environment';
import { HttpService } from '../../http.service';
import { GetSubscriptionPlan } from 'src/app/interfaces/services/subscription.interface';
import { NcacheSubscription } from 'src/app/models/ncache-subscription.model';
import { HasSubscription } from 'src/app/interfaces/services/subscription.interface';
import { GetSubscription } from 'src/app/interfaces/services/subscription.interface';
import { ActivateSubscription } from 'src/app/interfaces/services/subscription.interface';
import { AddReservationRequest } from '../../models/add-reservation-request.model';
import { IpLocationResponse } from 'src/app/models/responses/ip-location-response.model';
import { GetMinimumServerOnlyLicensesResponse } from 'src/app/models/responses/get-minimum-server-only-licenses-response.model';
import { GetMinimumServerLicensesResponse } from 'src/app/models/responses/get-minimum-server-licenses-response.model';
import { GetMinimumClientLicensesResponse } from 'src/app/models/responses/get-minimum-client-licenses-response.model';
import { GetSubscriptionStatusResponse } from 'src/app/models/responses/get-subscription-status-response.model';
import { SubscriptionEdition } from 'src/app/enums/subscription-edition.enum';
import { LicensePlan } from 'src/app/models/license-plan.model';
import { ExtendTrialRequest } from 'src/app/models/requests/extend-trial-request';
import { SingleVmOperations } from 'src/app/interfaces/services/vm.interface';
import { VirtualMachineUpdate } from 'src/app/models/virtual-machine-update.model';
import { NcacheVersion } from 'src/app/models/ncache-version.model';
import { NcacheAwsImageResponse } from 'src/app/models/ncache-aws-image-response';
import { NcacheAzureImageResponse } from 'src/app/models/ncache-azure-image-response';
import { formatDate } from '@angular/common';
import { LicenseGroup } from 'src/app/models/responses/license-group';
import { RegisterUserFromLeadRequest } from 'src/app/models/RegisterUserFromLeadRequest';
import { EmailVerifiedRequest } from 'src/app/models/EmailVerifiedRequest';
import { TransactionHistoryEntry } from 'src/app/models/transaction-history-entry.model';
import { ActivationDetail, MarketPlaceOffer, ServerActvLink, SubscriptionPlansResponse } from 'src/app/interfaces/plan.interface';
import { CreateUserRequest } from 'src/app/interfaces/user.interface';
import { CreateLicenseKeyRequest, CreateSubscriptionRequest, CreateSubscriptionResponse, Enable24x7Request, NCacheSubscription, TrialEmailsRequest, UserSubscriptionResponse } from 'src/app/interfaces/subscription.interface';
import { Transaction } from 'src/app/interfaces/transaction.interface';
import { ItemsList } from '@ng-select/ng-select/lib/items-list';
import { MonthlyEnvironmentCost } from 'src/app/interfaces/monthly-environment-cost.interface';

@Injectable({
  providedIn: 'root',
})

export class CloudSubscriptionService
  implements
  CreateSubscription,
  GetSigninUrl,
  GetSubscriptionPlan,
  HasSubscription,
  GetSubscription,
  ActivateSubscription,
  SingleVmOperations {
  activatePendingSubscriptions(): Observable<boolean> {
    return this.http.post(
      environment.api.routes.subscription.activate_awaiting_subscriptions,
      {}
    );
  }

  getVersion(editionId: any): Observable<NcacheVersion> {
    return this.http.get(
      environment.api.routes.subscription.paid.get_version_from_edition,
      { editionId }
    );
  }

  monthNames: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  trialStatus: string;

  formatExpiryDate(cloudSubscription: NcacheSubscription): string {
    if (cloudSubscription != null) {
      if (this.isFreeTrialSubscription(cloudSubscription)) {
        let expiryDate: Date;
        if (cloudSubscription.status === 'trial_expired') {
          this.trialStatus = 'EXPIRED on ';
          expiryDate = new Date(
            cloudSubscription.ncacheSubscriptionPeriod[0].freeTrialExpiry
          );
        } else if (cloudSubscription.status === 'trial_terminated') {
          this.trialStatus = 'TERMINATED on ';
          expiryDate = new Date(
            cloudSubscription.ncacheSubscriptionPeriod[0].FreeTrialTerminatedOn
          );
        } else {
          this.trialStatus = 'EXPIRES on ';
          expiryDate = new Date(
            cloudSubscription.ncacheSubscriptionPeriod[0].freeTrialExpiry
          );
        }

        return this.formatDate(expiryDate);
      }
    }

    return '';
  }

  formatDate(date: any): string {
    return formatDate(date, 'dd-MMM-yyyy', 'en-US');
  }

  isFreeTrialSubscription(cloudSubscription: NcacheSubscription): boolean {
    if (cloudSubscription == null) return false;

    if (cloudSubscription.status === 'active') return true;
    else if (
      cloudSubscription.status !== 'active' &&
      cloudSubscription.ncacheSubscriptionPeriod[0].freeTrialExpiry != null
    )
      return true;
    else return false;
  }

  extendTrial(request: ExtendTrialRequest): Observable<any> {
    return this.http.put(
      environment.api.routes.subscription.extend_trial,
      request
    );
  }

  verifyOTP(email: string, otp: string): Observable<string> {
    return this.http.post(environment.api.routes.subscription.verify_otp, { email, otp });
  }

  resendOTP(email: string): Observable<string> {
    return this.http.post(environment.api.routes.subscription.resend_otp, { email });
  }

  isNextPeriodRecurringDiscountNew(): Observable<boolean> {
    return this.http.get(
      environment.api.routes.subscription.is_next_period_recurring_discount_new
    );
  }
  modifyNextSubscription(request: AddReservationRequest): Observable<any> {
    return this.http.post(
      environment.api.routes.subscription.modify_next_subscription,
      request
    );
  }
  modifyExistingSubscription(request: AddReservationRequest): Observable<any> {
    return this.http.post(
      environment.api.routes.subscription.modify_existing_subscription,
      request
    );
  }
  getExistingDimensionCosts(couponNo): Observable<any> {
    return this.http.get(
      environment.api.routes.subscription.get_existing_dimension_discount,
      { couponNo }
    );
  }
  constructor(
    private http: HttpService,
    private authSvc: CloudAuthenticationService
  ) { }

  getFutureSubsriptionPeriod(): Observable<any> {
    return this.http.get(
      environment.api.routes.subscription.get_future_subscription_period
    );
  }

  getMktPlace(subscriptionTitle: string): Observable<any> {
    console.log("get marketPlace")
    console.log(subscriptionTitle);
    return this.http.get(
      environment.api.routes.subscription.get_mktplace,
      { 'subscriptionTitle': subscriptionTitle, 'api-version': '2024-11-01' }
    );
  }

  getAllSubscriptionPeriods(): Observable<any> {
    return this.http.get(
      environment.api.routes.subscription.get_all_subscription_periods
    );
  }
  getDimensionCosts(cloudplanId, couponNo): Observable<any> {
    return this.http.get(
      environment.api.routes.subscription.get_subscription_dimension_cost,
      { cloudplanId, couponNo }
    );
  }

  resolveAndGetMarketplaceId(req: MarketplaceTokenRequest): Observable<{ id: number, period: string }> {
    return this.http.post<MarketPlaceOffer>(environment.api.routes.subscription.get_mktplace_id, req).pipe(
      catchError(error => this.handleError(error))
    );
  }


  getLicensePlanRequest(vendorId: string, productName: string, licenseType: string): Observable<LicensePlan[]> {
    return this.http.get(environment.api.routes.environment.get_license_plan_data, { vendorId, productName, licenseType });
  }

  getSubscriptionCost(): Observable<any> {
    return this.http.get(
      environment.api.routes.subscription.get_subscription_cost
    );
  }

  getTransactions(subscriptionId: string) {
    return this.http.get<Transaction[]>(environment.api.routes.subscription.transaction.get_transactions, { subscriptionId })
  }

  getReservedTransactions(subscriptionId: string) {
    return this.http.get<Transaction[]>(environment.api.routes.subscription.transaction.get_reservation_transactions, { subscriptionId })
  }

  getMonthlyUsageTransactions(subscriptionId: string) {
    return this.http.get<any>(environment.api.routes.subscription.transaction.get_monthly_usage_transactions, { subscriptionId })
  }

  getSubsriptionRemainingTime(): Observable<any> {
    return this.http.get(
      environment.api.routes.subscription.get_subscription_remaining_time
    );
  }
  getSubscriptionEdition(): Observable<SubscriptionEdition> {
    return this.http
      .get<string>(
        environment.api.routes.subscription.get_subscription_edition,
        {},
        'string'
      )
      .pipe(
        map((edition) => {
          return SubscriptionEdition[edition];
        })
      );
  }

  getPlanFromMarketPlaceToken(token: string, vendor: string): Observable<MarketPlaceOffer> {
    return this.http.post(environment.api.routes.subscription.resolve_marketplace_token, { token: token, vendor: vendor })
  }
  getServerActvLink(subscriptionId: string): Observable<ServerActvLink> {
    return this.http.get(environment.api.routes.subscription.License.get_server_actv_link, { subscriptionid: subscriptionId });
  }

  getDiscount(couponNo): Observable<any> {
    return this.http.get(environment.api.routes.subscription.get_subscription_discount, { couponNo });
  }
  getPlanDetailFromState(req: MarketplaceTokenRequest): Observable<MarketPlaceOffer> {
    return this.http.post<MarketPlaceOffer>(environment.api.routes.subscription.get_plan_details_from_state, req).pipe(
      catchError(error => this.handleError(error))
    );
  }

  getLatestSusbcriptionPeriod(): Observable<any> {
    return this.http.get(
      environment.api.routes.subscription.get_latest_subscription_period
    );
  }

  registerUser(createUserRequest: CreateUserRequest): Observable<string> {
    return this.http.post(environment.api.routes.user.register, { primaryUser: createUserRequest.primaryUser, secondaryUser: createUserRequest.secondaryUser, companyDetails: createUserRequest.company });
  }

  registerUserFromLead(req: RegisterUserFromLeadRequest): Observable<boolean> {
    return this.http.post(environment.api.routes.user.register_lead, req);
  }

  isEmailVerified(req: EmailVerifiedRequest): Observable<boolean> {
    return this.http.post(environment.api.routes.user.is_email_verified, req);
  }

  createSubscription(createSubscriptionRequest: CreateSubscriptionRequest): Observable<{ subscription: CreateSubscriptionResponse }> {
    return this.http.post(environment.api.routes.subscription.create, createSubscriptionRequest);
  }
  createLicenseKey(createSubscriptionRequest: CreateLicenseKeyRequest): Observable<{ subscription: any }> {
    return this.http.post(environment.api.routes.subscription.License.create_License, createSubscriptionRequest);
  }

  extendLicenseEmail(request: TrialEmailsRequest): Observable<any> {
    return this.http.post(
      environment.api.routes.subscription.email.extend_trail_email,
      request
    );
  }
  getLicenseEmail(request: TrialEmailsRequest): Observable<any> {
    return this.http.post(
      environment.api.routes.subscription.email.get_license_email,
      request
    );
  }

  hasSubscription(): Observable<string> {
    return this.http
      .get<string>(environment.api.routes.subscription.has_subscription, {})
      .pipe(catchError((error) => this.handleError(error)));
  }

  getSubscriptionPlan(subscriptionId: string): Observable<CloudPlan> {
    return this.http
      .get<CloudPlan>(
        environment.api.routes.subscription.get_subscription_plan,
        { subscriptionUID: subscriptionId }
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  getSubscription(subscriptionUid?: string): Observable<NcacheSubscription> {
    const dummyNcacheSubscription: NcacheSubscription = {
      id: '55867b4f-66ed-49c7-8b67-e341c50295fb',
      alAccountId: 1001,
      status: 'active',
      planPeriod: 'yearly',
      isFreeTrialInExtension: false,
      cloudPlanId: 2001,
      marketplacePurchaseId: 'mp-56789',
      cloudPlan: {
        id: 3001,
        cloudVendorId: 4001,
        vendorPlanId: 'vp-001',
        title: 'Enterprise Plan',
        description: 'This is the enterprise plan offering advanced features.',
        includedServerLicenses: 10,
        includedClientLicenses: 20,
        maxServerLicenses: 100,
        maxClientLicenses: 200,
        isActive: 1,
        isFreeTrial: false,
        monthlyOneTimeCost: 500,
        yearlyOneTimeCost: 5000,
        additionalServerLicenseCost: 50,
        additionalClientLicenseCost: 25,
        period: 'yearly',
        productId: null,
        vendorName: 'AWS',
        isHosted: 1,
        cloudPlansNcacheVersions: null,
        cloudPlansVmSizes: null,
        cloudVendor: null,
        dimension: null,
        ncacheSubscription: null,
        twentyFoursevenSupport: true,
      },
      cloudVendor: {
        id: 4001,
        displayName: 'AWS',
        vendorName: 'AWS',
      },
      freeTrialNcacheImage: null,
      ncacheSubscriptionPeriod: [
        {
          id: 5001,
          ncacheSubscriptionId: 1001,
          startTime: '2024-01-01T00:00:00Z',
          endTime: null,
          freeTrialExpiry: null,
          FreeTrialTerminatedOn: null,
          usedServerLicenses: 5,
          usedClientLicenses: 10,
          reservedServerLicenses: 2,
          reservedClientLicenses: 3,
          reservedServerOnlyLicenses: 1,
          reportedServerLicenses: 6,
          reportedClientLicenses: 8,
          supportEnabled: null,
          is24x7Enabled: 1,
          serverLicensesSupport: 2,
          clientLicensesSupport: 4,
          reportedServerLicensesSupport: 1,
          reportedClientLicensesSupport: 3,
          serverLicenses24x7: 2,
          clientLicenses24x7: 4,
          reportedServerLicenses24x7: 1,
          reportedClientLicenses24x7: 3,
          lastReportedTime: null,
          createTime: '2024-01-01T00:00:00Z',
          updateTime: null,
          deleted: 0,
          ncacheSubscription: null,
        },
      ],
      deploymentUser: null,
      key: 'sub-key-123',
      pending: false,
      has24x7Support: true,
    };
    return of(dummyNcacheSubscription).pipe(delay(100));
    //return this.http.get<NcacheSubscription>(environment.api.routes.subscription.get_subscription_by_uid, { subscriptionUID: subscriptionUid })
  }

  getSubscriptionForDashboard(
    subscriptionUid?: string
  ): Observable<NcacheSubscription> {
    return this.http.get<NcacheSubscription>(
      environment.api.routes.subscription.get_subscription_by_uid,
      { subscriptionUID: subscriptionUid }
    );
  }

  getSubscriptionsListByVendors(): Observable<UserSubscriptionResponse> {
    return this.http.get(environment.api.routes.subscription.get_subscriptions_by_vendor);
  }

  getSubscriptionStatus(): Observable<GetSubscriptionStatusResponse> {
    return this.http.get<GetSubscriptionStatusResponse>(
      environment.api.routes.subscription.get_subscription_status
    );
  }

  handleError(error: HttpErrorResponse): Observable<any> {
    return throwError(error.error);
  }

  getSignInUrl(vendorName: string) {
    return this.authSvc.getSignInUrl(vendorName);
  }

  activateSubscription(): Observable<boolean> {
    return this.http.put(environment.api.routes.subscription.activate, {});
  }

  getIpLocation(): Observable<IpLocationResponse> {
    return this.http.get(environment.api.routes.subscription.get_ip_location);
  }

  getMinimumServerLicenses(): Observable<GetMinimumServerLicensesResponse> {
    return this.http.get(
      environment.api.routes.subscription.get_minimum_server_licenses
    );
  }

  getMinimumClientLicenses(): Observable<GetMinimumClientLicensesResponse> {
    return this.http.get(
      environment.api.routes.subscription.get_minimum_client_licenses
    );
  }

  getMinimumServerOnlyLicenses(): Observable<GetMinimumServerOnlyLicensesResponse> {
    return this.http.get(
      environment.api.routes.subscription.get_minimum_server_only_licenses
    );
  }

  getTotalLicensesCost(): Observable<number> {
    return this.http.get(
      environment.api.routes.subscription.get_total_licenses_cost
    );
  }

  getVmPublicIp(
    vmId: number,
    deploymentUserId: number,
    regionName: string
  ): Observable<{ ipAddress: string; status: string }> {
    return this.http.get(environment.api.routes.environment.getVmPublicIp, {
      vmId,
      deploymentUserId,
      regionName,
    });
  }

  getVMStatus(subscriptionId: string): Observable<VirtualMachineUpdate[]> {
    return this.http.get<VirtualMachineUpdate[]>(
      environment.api.routes.get_vm_status,
      { subscriptionId }
    );
  }

  startVm(subscriptionId: string, vmIds: number[]) {
    return this.http.post<any>(environment.api.routes.subscription.vm.start, {
      vmIds,
      subscriptionId,
    });
  }

  stopVm(subscriptionId: string, vmIds: number[]) {
    return this.http.post<any>(environment.api.routes.subscription.vm.stop, {
      vmIds,
      subscriptionId,
    });
  }

  restartVm(subscriptionId: string, vmIds: number[]) {
    return this.http.post<any>(environment.api.routes.subscription.vm.restart, {
      vmIds,
      subscriptionId,
    });
  }

  deleteVm(subscriptionId: string, vmIds: number[]): Observable<any[]> {
    return this.http.post<any>(environment.api.routes.subscription.vm.delete, {
      vmIds,
      subscriptionId,
    });
  }

  getAzureFreeTrialImages(
    subscriptionUid: string,
    vendorName: string
  ): Observable<NcacheAzureImageResponse[]> {
    return this.http.get<NcacheAzureImageResponse[]>(
      environment.api.routes.environment.get_free_trial_ncache_image_for_vendor,
      { subscriptionUid, vendorName }
    );
  }

  getAwsFreeTrialImages(
    subscriptionUid: string,
    vendorName: string
  ): Observable<NcacheAwsImageResponse[]> {
    return this.http.get<NcacheAwsImageResponse[]>(
      environment.api.routes.environment.get_free_trial_ncache_image_for_vendor,
      { subscriptionUid, vendorName }
    );
  }

  getLicenseKeysForPaidSubscription(
    accountId: Number,
    subscriptionId: string,
    editionId: string,
    vendorName: string,
    licenseType: string
  ): Observable<LicenseGroup> {
    return this.http.get<LicenseGroup>(
      environment.api.routes.subscription.paid.get_licenses_for_version,
      {
        accountId,
        subscriptionId,
        editionId,
        vendor: vendorName,
        type: licenseType,
      }
    );
  }

  hasFreeTrialSubscriptions(): Observable<boolean> {
    return of(false);
    return this.http.get(
      environment.api.routes.subscription.has_Free_Trial_Subscriptions
    );
  }
  checkIfHosted(subscriptionId: string): Observable<boolean> {
    // var url = Helper.formatString(environment.api.routes.subscription.is_hosted, subscriptionId);
    // return this.http.get<boolean>(url);
    const hosted = true;
    return of(hosted);
  }

  getSubscriptionPlans(subscriptionPlansRequest: SubscriptionPlansRequest): Observable<SubscriptionPlansResponse> {
    return this.http.get(environment.api.routes.get_available_plans, { Id: subscriptionPlansRequest.subscriptionId, period: subscriptionPlansRequest.period });
  }
  getMonthlyUsageData(subscriptionId: string, month: Date): Observable<MonthlyEnvironmentCost[]> {
    return this.http.get(environment.api.routes.subscription.License.get_monthly_usage_data, { subscriptionid: subscriptionId, month: month.toISOString() });
  }
  getMonthlyUsageMonths(subscriptionId: string): Observable<string[]> {
    return this.http.get(environment.api.routes.subscription.License.get_monthly_usage_months, { subscriptionid: subscriptionId });
  }

  confirmSelectedPlan(request: ActivateSubscriptionRequest): Observable<boolean> {
    return this.http.post(environment.api.routes.select_plan, request);
  }

  getSubscriptionDetail(subscriptionId: string): Observable<NCacheSubscription> {
    return this.http.get(environment.api.routes.subscription.get_subscription_detail, { Id: subscriptionId })
  }

  getActivationDetail(subscriptionId: string): Observable<any> {
    var res = this.http.get<ActivationDetail[]>(
      environment.api.routes.subscription.License.get_Activation_details,{ subscriptionid: subscriptionId }
    )
    return res;
  }

  userHasSubscription() {
    return this.http.get<boolean>(environment.api.routes.subscription.has_subscription)
  }

  enable24x7Payg(data: Enable24x7Request) {
    return this.http.put<string>(environment.api.routes.subscription.enable24x7, data)
  }

  check24x7Exists(subscriptionId: string) {
    return this.http.get<boolean>(environment.api.routes.subscription.check_24x7_exists, { subscriptionId })
  }
}
